import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { cartApi } from '@/api/service';
import { parsePrice } from '@/config';
import type { CartItem, CartState } from '@/types';
import { logger } from '@/utils/logger';

interface CartOperation {
  type: 'add' | 'remove' | 'update' | 'clear';
  itemId?: string;
  item?: CartItem;
  quantity?: number;
  timestamp: number;
}

interface CartStore extends CartState {
  isLoading: boolean;
  error: string | null;
  isSyncing: boolean;
  pendingOperations: CartOperation[];

  fetchCart: () => Promise<void>;
  addToCart: (item: CartItem) => Promise<void>;
  removeFromCart: (id: string) => Promise<void>;
  updateQuantity: (id: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  syncWithBackend: () => Promise<void>;
  mergeGuestCart: () => Promise<void>;
}

const calculateTotals = (items: CartItem[]) => {
  const total = items.reduce((sum, item) => sum + parsePrice(item.price) * item.quantity, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  return { total, itemCount };
};

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      cartId: undefined,
      items: [],
      total: 0,
      itemCount: 0,
      isLoading: false,
      error: null,
      isSyncing: false,
      pendingOperations: [],

      fetchCart: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await cartApi.getCart();
          if (response.success && response.data) {
            const items = response.data.items.map(item => ({
              ...item.product,
              quantity: item.quantity,
              selectedColor: item.selectedColor,
              selectedSize: item.selectedSize,
            })) as CartItem[];
            set({
              cartId: response.data.id,
              items,
              ...calculateTotals(items),
              error: null,
            });
          }
        } catch {
          set({ error: 'Failed to load cart' });
        } finally {
          set({ isLoading: false });
        }
      },

      addToCart: async (item: CartItem) => {
        const { items, isSyncing } = get();
        if (isSyncing) return;

        if (!item?.id || !item?.quantity || item.quantity <= 0) {
          set({ error: 'Invalid item data' });
          return;
        }

        const previousState = { items: [...items], total: get().total, itemCount: get().itemCount };
        const existingItem = items.find(i => i.id === item.id);

        const newItems = existingItem
          ? items.map(i => (i.id === item.id ? { ...i, quantity: i.quantity + item.quantity } : i))
          : [...items, item];

        set({
          items: newItems,
          ...calculateTotals(newItems),
          isSyncing: true,
          error: null,
        });

        try {
          const response = await cartApi.addToCart({
            productId: item.id,
            quantity: item.quantity,
            selectedColor: item.selectedColor,
            selectedSize: item.selectedSize,
          });

          if (!response.success) {
            set(previousState);
            set({ error: response.error?.message || 'Failed to add item' });
          }
        } catch {
          set(previousState);
          set({ error: 'Sync failed' });
        } finally {
          set({ isSyncing: false });
        }
      },

      removeFromCart: async (id: string) => {
        const { items, isSyncing } = get();
        if (isSyncing) return;

        const previousState = { items: [...items], total: get().total, itemCount: get().itemCount };
        const newItems = items.filter(i => i.id !== id);

        set({
          items: newItems,
          ...calculateTotals(newItems),
          isSyncing: true,
          error: null,
        });

        try {
          const response = await cartApi.removeFromCart(id);
          if (!response.success) {
            set(previousState);
            set({ error: response.error?.message || 'Failed to remove item' });
          }
        } catch {
          set(previousState);
          set({ error: 'Sync failed' });
        } finally {
          set({ isSyncing: false });
        }
      },

      updateQuantity: async (id: string, quantity: number) => {
        const { items, isSyncing } = get();
        if (isSyncing) return;

        if (quantity <= 0) {
          void get().removeFromCart(id);
          return;
        }

        const previousState = { items: [...items], total: get().total, itemCount: get().itemCount };
        const newItems = items.map(i => (i.id === id ? { ...i, quantity } : i));

        set({
          items: newItems,
          ...calculateTotals(newItems),
          isSyncing: true,
          error: null,
        });

        try {
          const response = await cartApi.updateCartItem({ itemId: id, quantity });
          if (!response.success) {
            set(previousState);
            set({ error: response.error?.message || 'Failed to update quantity' });
          }
        } catch {
          set(previousState);
          set({ error: 'Sync failed' });
        } finally {
          set({ isSyncing: false });
        }
      },

      clearCart: async () => {
        if (get().isSyncing) return;

        const previousState = {
          items: [...get().items],
          total: get().total,
          itemCount: get().itemCount,
        };
        set({ items: [], total: 0, itemCount: 0, isSyncing: true, error: null });

        try {
          const response = await cartApi.clearCart();
          if (!response.success) {
            set(previousState);
            set({ error: response.error?.message || 'Failed to clear cart' });
          }
        } catch {
          set(previousState);
          set({ error: 'Sync failed' });
        } finally {
          set({ isSyncing: false });
        }
      },

      syncWithBackend: async () => {
        if (get().isLoading) return;
        await get().fetchCart();
      },

      mergeGuestCart: async () => {
        const { items } = get();
        if (!items.length) return;

        set({ isSyncing: true, error: null });

        try {
          const results = await Promise.allSettled(
            items.map(item =>
              cartApi.addToCart({
                productId: item.id,
                quantity: item.quantity,
                selectedColor: item.selectedColor,
                selectedSize: item.selectedSize,
              })
            )
          );

          const failed = results.filter(
            r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success)
          );
          if (failed.length > 0) {
            logger.warn(`Guest cart merge: ${failed.length} items failed to sync`, {
              totalItems: items.length,
            });
          }

          await get().fetchCart();
        } catch (err) {
          logger.error('Guest cart merge failed', { error: err });
          set({ error: 'Failed to merge cart. Please try again.' });
        } finally {
          set({ isSyncing: false });
        }
      },
    }),
    {
      name: 'bst_cart_storage',
      storage: createJSONStorage(() => window.localStorage),
    }
  )
);
